import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../pages/styles/AlertPage.css";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandMore from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";

function AlertPage() {
  const { t } = useTranslation();

  const alertsData = [
    { id: 0, title: t("All") },
    { id: 1, title: t("Weather Data") },
    { id: 2, title: t("Water Level Data") },
    { id: 3, title: t("Light Control Data") },
    { id: 4, title: t("CCTV Data") },
    { id: 5, title: t("LED Screen Data") },
    { id: 6, title: t("Wildfire Data") },
    { id: 7, title: t("Wildlife Data") },
  ];

  const [expandedStates, setExpandedStates] = useState(
    alertsData.reduce((acc, alert) => ({ ...acc, [alert.id]: false }), {})
  );
  const [selectedTabId, setSelectedTabId] = useState(0);
  const [lightControlData, setLightControlData] = useState([]);
  const [viewedStates, setViewedStates] = useState(
    alertsData.reduce((acc, alert) => ({ ...acc, [alert.id]: false }), {})
  );

  useEffect(() => {
    const storedData = JSON.parse(sessionStorage.getItem("auditRecords")) || [];
    // สลับลำดับของข้อมูลเพื่อให้อ่านจากล่างสุดขึ้นมา
    setLightControlData(storedData.reverse());
  }, []);

  const toggleExpand = (id) => {
    setExpandedStates((prevStates) => ({
      ...prevStates,
      [id]: !prevStates[id],
    }));

    if (!viewedStates[id]) {
      setViewedStates((prevStates) => ({
        ...prevStates,
        [id]: true,
      }));
    }
  };

  const calculateTimeDifference = (timestamp) => {
    const now = new Date();

    // แยกส่วนของวันที่และเวลา
    const [datePart, timePart] = timestamp.split(" ");
    const [day, month, year] = datePart.split("/");
    const [hours, minutes, seconds] = timePart.split(":");

    // สร้าง Date object จากข้อมูลที่แยกแล้ว
    const eventTime = new Date(
      parseInt(year) - 543,
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hours),
      parseInt(minutes),
      parseInt(seconds)
    );

    const diffInMinutes = Math.floor((now - eventTime) / (1000 * 60));

    if (diffInMinutes < 1) {
      return "เมื่อสักครู่";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} นาทีที่แล้ว`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} ชั่วโมงที่แล้ว`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} วันที่แล้ว`;
    }
  };

  const renderAlertContent = (alertId) => {
    if (alertId === 3 && lightControlData.length > 0) {
      return lightControlData.map((data, index) => (
        <div key={index} className="alert-detail-item">
          <div className="alert-header">
            <span className="alert-timestamp">
              {calculateTimeDifference(data.timestamp)}
            </span>
            <span className="alert-full-timestamp">{data.timestamp}</span>
          </div>
          <div className="alert-body">
            <div className="alert-info-group">
              <div className="alert-info">
                <span className="alert-label">User ID:</span>
                <span className="alert-value">{data.payload.user_id}</span>
              </div>
              <div className="alert-info">
                <span className="alert-label">Pole ID:</span>
                <span className="alert-value">{data.payload.pole_id}</span>
              </div>
            </div>
            <div className="alert-info-group">
              <div className="alert-info">
                <span className="alert-label">Command Type:</span>
                <span className="alert-value">{data.payload.command_type}</span>
              </div>
              <div className="alert-info">
                <span className="alert-label">Hardware ID:</span>
                <span className="alert-value">
                  {data.payload.payload.hw_id}
                </span>
              </div>
            </div>
            <div className="alert-info">
              <span className="alert-label">Value:</span>
              <span className="alert-value">{data.payload.payload.value}</span>
            </div>
          </div>
        </div>
      ));
    } else {
      const alertTitle = alertsData.find(
        (alert) => alert.id === alertId
      )?.title;
      return (
        <div className="alert-detail-item no-alerts">
          <p>{`ไม่มีการแจ้งเตือนเกี่ยวกับ${
            alertTitle ? alertTitle.toLowerCase() : ""
          }`}</p>
        </div>
      );
    }
  };

  const hasAlerts = (alertId) => {
    if (alertId === 0 || alertId === 3) {
      return lightControlData.length > 0;
    }
    return false;
  };

  const filteredAlerts = alertsData.filter(
    (alert) =>
      alert.id !== 0 &&
      (selectedTabId === 0 ? hasAlerts(alert.id) : alert.id === selectedTabId)
  );

  return (
    <div className="container-alert">
      <h1>{t("Alerts")}</h1>
      <div className="navbarAlert">
        {alertsData.map((alert) => (
          <button
            key={alert.id}
            className={`nav-item ${selectedTabId === alert.id ? "active" : ""}`}
            onClick={() => setSelectedTabId(alert.id)}
          >
            {alert.title}
          </button>
        ))}
      </div>
      {filteredAlerts.length === 0 ? (
        <div className="no-alerts">
          <NotificationsOffIcon sx={{ fontSize: 60, color: "#8c8c8c" }} />
          <p>ไม่มีรายการแจ้งเตือน</p>
        </div>
      ) : (
        <List
        sx={(theme) => ({
          width: '90vw',
          bgcolor: "#344054",
          borderRadius: "10px",
          overflow: "hidden",
          marginTop: "5rem",
          [theme.breakpoints.up('sm')]: {
            width: '100%',
            marginTop: "2rem",
          },
        })}
      >
          {filteredAlerts.map((alert) => (
            <React.Fragment key={alert.id}>
              <ListItem
                button
                onClick={() => toggleExpand(alert.id)}
                sx={{
                  backgroundColor:
                    hasAlerts(alert.id) && !viewedStates[alert.id]
                      ? "#ff4d4f"
                      : "transparent",
                  color: "white",
                  borderRadius: 1,
                  mb: 1,
                  "&:hover": {
                    backgroundColor:
                      hasAlerts(alert.id) && !viewedStates[alert.id]
                        ? "#ff7875"
                        : "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                <WarningIcon sx={{ marginRight: 2 }} />
                <ListItemText
                  primary={
                    alert.id === 3
                      ? `${alert.title} : ปรับค่าความสว่าง`
                      : alert.title
                  }
                  primaryTypographyProps={{
                    fontFamily: '"IBM Plex Sans Thai", sans-serif',
                    fontWeight: "bold",
                    color: "#FFFFFF",
                  }}
                />
                <IconButton edge="end" sx={{ color: "#FFFFFF" }}>
                  <ExpandMore
                    sx={{
                      transform: expandedStates[alert.id]
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </IconButton>
              </ListItem>

              <Collapse
                in={expandedStates[alert.id]}
                timeout="auto"
                unmountOnExit
              >
                <div className="alert-details">
                  {renderAlertContent(alert.id)}
                </div>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      )}
    </div>
  );
}

export default AlertPage;
